import React, { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { FiX } from 'react-icons/fi';
import { usePrefersReducedMotion } from '../hooks/useMediaQuery';
import { cn } from '../utils/cn';
import { TOUR_UI_ATTR, isFullyInView, isPinned } from './tourDom';
import { placeCard, spotlightRect, type Rect, type Size } from './tourLogic';
import type { TourStep } from './tourTypes';

const HEADER_INSET = 72;

interface TourOverlayProps {
  step: TourStep;
  index: number;
  total: number;
  target: HTMLElement | null;
  /** False while the next step's element is being looked up. */
  ready: boolean;
  isMobile: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

const readViewport = (): Size => ({ width: window.innerWidth, height: window.innerHeight });

const toRect = (el: Element): Rect => {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
};

/** Tracks the viewport and the target's on-screen rect through scrolls and resizes. */
function useTargetRect(target: HTMLElement | null) {
  const [state, setState] = useState<{ rect: Rect | null; viewport: Size }>(() => ({
    rect: null,
    viewport: readViewport(),
  }));

  useEffect(() => {
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setState({
          rect: target?.isConnected ? toRect(target) : null,
          viewport: readViewport(),
        });
      });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { capture: true, passive: true });
    const resizeObserver = new ResizeObserver(measure);
    if (target) resizeObserver.observe(target);
    resizeObserver.observe(document.body);
    // Catches movement from animations and late layout shifts that fire no event.
    const interval = window.setInterval(measure, 400);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, { capture: true });
      resizeObserver.disconnect();
      window.clearInterval(interval);
    };
  }, [target]);

  return state;
}

function useElementSize(el: HTMLElement | null): Size | null {
  const [size, setSize] = useState<Size | null>(null);
  useEffect(() => {
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const box = entry.borderBoxSize?.[0];
      setSize(
        box
          ? { width: box.inlineSize, height: box.blockSize }
          : { width: el.offsetWidth, height: el.offsetHeight },
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);
  return size;
}

export const TourOverlay: React.FC<TourOverlayProps> = ({
  step,
  index,
  total,
  target,
  ready,
  isMobile,
  onNext,
  onBack,
  onSkip,
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const maskId = useId().replace(/:/g, '');
  const titleId = `${maskId}-title`;
  const bodyId = `${maskId}-body`;
  const [card, setCard] = useState<HTMLDivElement | null>(null);
  const cardSize = useElementSize(card);
  const shownTarget = ready ? target : null;
  const { rect, viewport } = useTargetRect(shownTarget);
  const spot = rect ? spotlightRect(rect, viewport) : null;
  const isLast = index === total - 1;
  const title = (isMobile && step.mobileTitle) || step.title;
  const body = (isMobile && step.mobileBody) || step.body;
  const transition = reducedMotion
    ? { duration: 0 }
    : { type: 'spring' as const, stiffness: 380, damping: 34 };

  useEffect(() => {
    if (!shownTarget || isPinned(shownTarget) || isFullyInView(shownTarget, HEADER_INSET)) return;
    shownTarget.scrollIntoView({
      block: 'center',
      inline: 'nearest',
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }, [shownTarget, reducedMotion]);

  useEffect(() => {
    if (ready && card) card.focus({ preventScroll: true });
  }, [ready, card, index]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onSkip();
        return;
      }
      if (!ready) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        onNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        onBack();
      } else if (e.key === 'Tab' && card) {
        const focusable = card.querySelectorAll<HTMLElement>('button:not([disabled])');
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const activeEl = document.activeElement;
        if (!card.contains(activeEl)) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && (activeEl === first || activeEl === card)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && activeEl === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [ready, card, onNext, onBack, onSkip]);

  const measured = cardSize ?? { width: 360, height: 200 };
  const position = isMobile ? null : placeCard(spot, measured, viewport, step.placement);
  const sheetAtTop =
    isMobile && !!spot && spot.top + spot.height > viewport.height - measured.height - 16;

  const cardBody = (
    <>
      <div className="flex items-start justify-between gap-3">
        <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
          Step {index + 1} of {total}
        </p>
        <button
          type="button"
          onClick={onSkip}
          aria-label="Close tour"
          className="-m-1 rounded-full p-1 text-zinc-500 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
        >
          <FiX size={16} aria-hidden />
        </button>
      </div>
      <h2 id={titleId} className="mt-1.5 text-[15px] font-semibold leading-snug text-white">
        {title}
      </h2>
      <p
        id={bodyId}
        className="mt-1.5 whitespace-pre-line text-[13px] leading-relaxed text-zinc-300"
      >
        {body}
      </p>
      <div className="mt-4 flex items-center gap-2">
        <div className="mr-auto flex gap-1" aria-hidden>
          {Array.from({ length: total }, (_, i) => (
            <span
              key={i}
              className={cn(
                'h-1.5 rounded-full transition-all',
                i === index ? 'w-4 bg-white' : 'w-1.5 bg-white/25',
              )}
            />
          ))}
        </div>
        {!isLast && (
          <button
            type="button"
            onClick={onSkip}
            className="rounded-lg px-2.5 py-1.5 text-[12.5px] text-zinc-400 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
          >
            Skip
          </button>
        )}
        {index > 0 && (
          <button
            type="button"
            onClick={onBack}
            className="rounded-lg border border-white/10 px-3 py-1.5 text-[12.5px] text-zinc-200 transition-colors hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
          >
            Back
          </button>
        )}
        <button
          type="button"
          onClick={onNext}
          className="rounded-lg bg-white px-3.5 py-1.5 text-[12.5px] font-semibold text-black transition-colors hover:bg-zinc-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          {isLast ? 'Done' : 'Next'}
        </button>
      </div>
    </>
  );

  return createPortal(
    <div {...{ [TOUR_UI_ATTR]: '' }} className="fixed inset-0 z-[10000]">
      <svg
        className="absolute inset-0 h-full w-full"
        width={viewport.width}
        height={viewport.height}
        aria-hidden
      >
        <defs>
          <mask id={maskId}>
            <rect width="100%" height="100%" fill="white" />
            {spot && (
              <motion.rect
                initial={false}
                animate={{ x: spot.left, y: spot.top, width: spot.width, height: spot.height }}
                transition={transition}
                rx={12}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <motion.rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.62)"
          mask={`url(#${maskId})`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={reducedMotion ? { duration: 0 } : { duration: 0.2 }}
        />
      </svg>

      {spot && (
        <motion.div
          className="pointer-events-none absolute rounded-xl ring-2 ring-white/70 shadow-[0_0_0_4px_rgba(255,255,255,0.08)]"
          initial={false}
          animate={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height }}
          transition={transition}
        />
      )}

      <div aria-live="polite" className="sr-only">
        {ready ? `Step ${index + 1} of ${total}: ${title}` : ''}
      </div>

      <AnimatePresence mode="wait">
        {ready && (
          <motion.div
            key={index}
            ref={setCard}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={bodyId}
            tabIndex={-1}
            initial={{ opacity: 0, y: isMobile ? (sheetAtTop ? -16 : 16) : 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={reducedMotion ? { duration: 0 } : { duration: 0.18 }}
            style={
              position
                ? {
                    top: position.top,
                    left: position.left,
                    visibility: cardSize ? 'visible' : 'hidden',
                  }
                : undefined
            }
            className={cn(
              'absolute border border-white/10 bg-[#161618] text-left shadow-[0_18px_48px_rgba(0,0,0,0.6)] focus:outline-none',
              isMobile
                ? cn(
                    'inset-x-0 px-5 pt-4',
                    sheetAtTop
                      ? 'top-0 rounded-b-2xl border-t-0 pb-4 pt-[max(1rem,env(safe-area-inset-top))]'
                      : 'bottom-0 rounded-t-2xl border-b-0 pb-[max(1rem,env(safe-area-inset-bottom))]',
                  )
                : 'w-[min(360px,calc(100vw-24px))] rounded-2xl p-4',
            )}
          >
            {cardBody}
          </motion.div>
        )}
      </AnimatePresence>
    </div>,
    document.body,
  );
};
